import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import Cookies from 'js-cookie';
import {
  APIGATEWAY,
  MEMBERSHIPTYPES,
} from 'lumen/utils/constants/general-constants';
import { PI_BREAKDOWNS } from 'lumen/utils/constants/pi-constant-values';
import generateFakeName from 'lumen/utils/parity-analytics/pa-generate-fake-name';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export default class MarketParityInsightHistoricalRoute extends Route {
  queryParams = {
    hotelId: { refreshModel: true },
    exportDate: { refreshModel: true },
    platform: { refreshModel: true },
  };

  @service ajax;
  @service errorTracking;
  @service('hotel-and-market') hotelAndMarketService;
  @service router;
  @service store;

  exportDateCache = trackedObject();
  isDemoMode = Cookies.get('demoModev3');

  /**
   * Requests the export dates by the current extractDate and hotelId
   * @param  {Object} params  The query params used in the route flow
   * @return {Array}          An array containing all possible export dates
   */
  fetchExportDatesTask = task({ restartable: true }, async (params) => {
    try {
      const { hotelId, platform } = params;
      const { extractDate, dateAggregation } = this.paramsFor(
        'market.parity.insight',
      );

      const cachedExportDates =
        this.exportDateCache[hotelId]?.[extractDate]?.[dateAggregation]?.[
          platform
        ];

      if (cachedExportDates) {
        return cachedExportDates;
      }

      const apiParams = {
        subscription_id: hotelId,
        extract_date: extractDate,
        date_aggregation: dateAggregation,
        platform,
      };

      // If this is a new request, fetch data...
      const exportDates = await this.ajax.request(
        formatUrl(
          '/apigateway/v1/app/parityanalytics/export_configs/',
          apiParams,
          this.ajax.host,
        ),
        {
          method: 'GET',
          dataType: 'json',
          contentType: 'application/json; charset=utf-8',
        },
      );

      // Also make sure to cache fetched data
      // Ensure default object is present
      if (!this.exportDateCache[hotelId]) {
        this.exportDateCache[hotelId] = {};
      }

      if (!this.exportDateCache[hotelId][extractDate]) {
        this.exportDateCache[hotelId][extractDate] = {};
      }

      if (!this.exportDateCache[hotelId][extractDate][dateAggregation]) {
        this.exportDateCache[hotelId][extractDate][dateAggregation] = {};
      }

      const sortedExportDates = exportDates.sort(
        (a, b) => dayjs(a.extract_date).unix() - dayjs(b.extract_date).unix(),
      );

      this.exportDateCache[hotelId][extractDate][dateAggregation][platform] =
        sortedExportDates;

      return sortedExportDates;
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  fetchParityPermissionTask = task({ restartable: true }, async (hotelId) => {
    try {
      const checkpermissionUrl = `${this.ajax.host}/api/v3/parity/checkpermission/?hotel_id=${hotelId}`;
      return await this.ajax.request(checkpermissionUrl);
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  fetchPeriodsWithParitiesTask = task(
    { restartable: true },
    async (params, hotel, sortedExportDates) => {
      try {
        const exportDateObject = this.getExportDateObject(
          sortedExportDates,
          params.exportDate,
        );

        const { los, persons, from_dates: fromDates } = exportDateObject;
        const { mealPlan, pos, rateType, showMemberRate } = this.paramsFor(
          'market.parity.insight',
        );

        const fromDateDays = fromDates.map((date) => dayjs(date));
        const minFromDate = dayjs.min(fromDateDays).format('YYYY-MM-DD');
        const maxFromDate = dayjs.max(fromDateDays).format('YYYY-MM-DD');

        const requestParams = {
          namespace: APIGATEWAY,
          typeKey: 'parities/analytics',
          persons: persons,
          platform: params.platform,
          // Filter on ota until BE stops returning Platform unspecified (RATE-888)
          ota: hotel
            .getParityAnalyticsOTAs(params.platform)
            .map((ota) => ota.id),
          los,
          pos,
          mealtype: mealPlan,
          rate_type: rateType,
          membershiptype: 0,
          extract_date: params.exportDate,
          from_date_range_start: minFromDate,
          from_date_range_end: maxFromDate,
          filter_meta_allow_major: true,
          group_meta_by: ['ota', 'channel'],
          hotel_id: hotel.id,
          meta: 'nested',
          base_membershiptype: showMemberRate
            ? MEMBERSHIPTYPES.MEMBER_ONLY
            : MEMBERSHIPTYPES.PUBLIC,
        };

        const periods = await this.store.query('period', requestParams);
        // Filter out gaps in from_dates (https://lighthouseintelligence.atlassian.net/browse/PI-1155?focusedCommentId=287741)
        return [...periods].filter((period) =>
          fromDates.includes(period.day.formattedDate),
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  afterModel(model) {
    const params = this.paramsFor(this.routeName);
    const availablePlatforms = model.hotel.parityAnalyticsPlatforms;

    if (!availablePlatforms.includes(params.platform)) {
      params.platform = availablePlatforms[0];
      this.router.replaceWith({ queryParams: params });
    }
  }

  attemptTransition(transition, params) {
    if (!transition.isAborted) {
      transition.abort();
      this.router.transitionTo(
        'market.parity.insight.historical',
        this.hotelAndMarketService.market,
        { queryParams: params },
      );
    }
  }

  /**
   * TEMP Transforms data directly from a hotel endpoint response and obscures real names
   * @param  {Object}   data  API response data
   */
  demoTransform(data) {
    if (this.isDemoMode) {
      data.hotelinfo.name = generateFakeName(
        data.id,
        PI_BREAKDOWNS.SUBSCRIPTION_ID,
      );
    }

    return data;
  }

  /**
   * Gets the associated exportDateObject for a given exportDate
   * @param  {Array}  exportDateObjects     List of export dates
   * @param  {String} currentExportDate     The exportDate to look for
   * @return {Object}                       An object containing otas, los, persons and the exportDate that was searched for
   */
  getExportDateObject(exportDateObjects, currentExportDate) {
    let result = null;

    exportDateObjects.some((exportDateObject) => {
      if (exportDateObject.extract_date === currentExportDate) {
        result = exportDateObject;
        return true;
      }

      return false;
    });

    return result;
  }

  async model(params, transition) {
    const hotel = this.demoTransform(
      this.store.peekRecord('hotel', params.hotelId) ??
        (await this.store.findRecord('hotel', params.hotelId, {
          reload: false,
        })),
    );
    const model = {
      hotel: hotel,
      parities: this.fetchPeriodsWithParitiesTask,
      parityPermission: this.fetchParityPermissionTask,
      exports: [],
    };

    model.parityPermission.perform(params.hotelId);

    const sortedExportDates = await this.fetchExportDatesTask.perform(params);
    model.exports = sortedExportDates;

    if (!sortedExportDates.length) {
      return model;
    }

    if (
      !params.exportDate ||
      !sortedExportDates
        .map((date) => date.extract_date)
        .includes(params.exportDate)
    ) {
      // This makes sure we transition to have the export date SET (not null)
      params.exportDate = sortedExportDates.at(-1).extract_date;
      this.attemptTransition(transition, params);
    } else {
      model.parities.perform(params, hotel, sortedExportDates);
    }

    return model;
  }
}
